/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { UserProfile } from '../../types';
import { StudentAvatar } from '../Common/StudentAvatar';
import { Scale, Search, ShieldAlert, ShieldCheck, Clock, AlertTriangle, Calendar, User, Mail, School } from 'lucide-react';
import { useInAppDialog } from '../Common/InAppDialog';
import { parseLocalDate, calculateAge, formatDateOfBirth } from '../../utils/timeUtils';

interface ComplianceTabContentProps {
  users: UserProfile[];
  onSelectUser: (user: UserProfile) => void;
}

export const ComplianceTabContent: React.FC<ComplianceTabContentProps> = ({ users, onSelectUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'minors' | 'turning18' | 'adults'>('all');

  // Calculate age and days until 18
  const studentData = useMemo(() => {
    const students = (users || []).filter(u => u && u.role === 'student');
    const today = new Date();

    return students.map(student => {
      const dobStr = student.dateOfBirth;
      if (!dobStr) {
        return {
          student,
          age: null,
          birthDate: null,
          daysUntil18: null,
          isMinor: null,
          statusText: 'No DOB provided'
        };
      }

      const birthDate = parseLocalDate(dobStr);
      if (!birthDate) {
        return {
          student,
          age: null,
          birthDate: null,
          daysUntil18: null,
          isMinor: null,
          statusText: 'Invalid DOB'
        };
      }

      // Calculate age
      const age = calculateAge(dobStr, today);

      // Calculate 18th birthday
      const eighteenthBirthday = new Date(birthDate.getFullYear() + 18, birthDate.getMonth(), birthDate.getDate());

      const diffTime = eighteenthBirthday.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const isMinor = age < 18;
      let statusText = isMinor ? `${diffDays} days until 18` : 'Adult (18+)';
      if (diffDays <= 0 && isMinor) {
        statusText = 'Turning 18 today!';
      }

      return {
        student,
        age,
        birthDate,
        daysUntil18: isMinor ? diffDays : 0,
        isMinor,
        statusText
      };
    });
  }, [users]);

  const filteredStudents = useMemo(() => {
    return studentData.filter(item => {
      const s = item.student;
      const matchesSearch = 
        (s.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.school || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.district || '').toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) return false;

      if (filterMode === 'minors') return item.isMinor === true;
      if (filterMode === 'adults') return item.isMinor === false;
      if (filterMode === 'turning18') return item.isMinor === true && item.daysUntil18 !== null && item.daysUntil18 <= 90;

      return true;
    }).sort((a, b) => {
      // Sort minors turning 18 soonest first
      if (a.daysUntil18 !== null && b.daysUntil18 !== null) {
        return a.daysUntil18 - b.daysUntil18;
      }
      return 0;
    });
  }, [studentData, searchTerm, filterMode]);

  const minorCount = studentData.filter(i => i.isMinor === true).length;
  const turningSoonCount = studentData.filter(i => i.isMinor === true && i.daysUntil18 !== null && i.daysUntil18 <= 90).length;
  const adultCount = studentData.filter(i => i.isMinor === false).length;

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-md overflow-hidden p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold border border-emerald-200">
              <Scale className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-black text-[#011d3c] tracking-tight uppercase">Student Age & Compliance Ledger</h2>
              <p className="text-xs text-slate-500 font-medium">
                Monitor student ages, statutory minor status, and countdowns to 18th birthdays for parental consent & labor compliance.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats Pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="px-3 py-1.5 bg-sky-50 border border-sky-200 rounded-xl text-xs font-bold text-sky-800 flex items-center gap-1.5">
            <span>Minors Under 18:</span>
            <span className="bg-sky-200/70 text-sky-900 px-1.5 py-0.5 rounded font-black">{minorCount}</span>
          </div>
          <div className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-xl text-xs font-bold text-amber-800 flex items-center gap-1.5">
            <span>Turning 18 Soon (&lt;90d):</span>
            <span className="bg-amber-200/70 text-amber-900 px-1.5 py-0.5 rounded font-black">{turningSoonCount}</span>
          </div>
          <div className="px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs font-bold text-emerald-800 flex items-center gap-1.5">
            <span>Adults (18+):</span>
            <span className="bg-emerald-200/70 text-emerald-900 px-1.5 py-0.5 rounded font-black">{adultCount}</span>
          </div>
        </div>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-200">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search students, school, email..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-[#0094c8]"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          <button
            type="button"
            onClick={() => setFilterMode('all')}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
              filterMode === 'all' 
                ? 'bg-[#0f2d4a] text-white shadow-xs' 
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
            }`}
          >
            All Students ({studentData.length})
          </button>
          <button
            type="button"
            onClick={() => setFilterMode('minors')}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
              filterMode === 'minors' 
                ? 'bg-sky-600 text-white shadow-xs' 
                : 'bg-white text-sky-700 border border-sky-200 hover:bg-sky-50'
            }`}
          >
            Minors ({minorCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterMode('turning18')}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
              filterMode === 'turning18' 
                ? 'bg-amber-600 text-white shadow-xs' 
                : 'bg-white text-amber-700 border border-amber-200 hover:bg-amber-50'
            }`}
          >
            Turning 18 Soon ({turningSoonCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterMode('adults')}
            className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
              filterMode === 'adults' 
                ? 'bg-emerald-600 text-white shadow-xs' 
                : 'bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-50'
            }`}
          >
            Adults ({adultCount})
          </button>
        </div>
      </div>

      {/* Student Age & Compliance Table */}
      <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-3xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-mono text-[10px] uppercase tracking-wider">
                <th className="py-3 px-4 font-black">Student Name</th>
                <th className="py-3 px-4 font-black">School & District</th>
                <th className="py-3 px-4 font-black">Date of Birth</th>
                <th className="py-3 px-4 font-black">Current Age</th>
                <th className="py-3 px-4 font-black">18th Birthday Countdown</th>
                <th className="py-3 px-4 font-black text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredStudents.length > 0 ? (
                filteredStudents.map(({ student, age, birthDate, daysUntil18, isMinor, statusText }) => {
                  const ferpaGranted = typeof student.ferpaConsent === 'object'
                    ? student.ferpaConsent?.granted === true
                    : student.ferpaConsent === true;

                  return (
                    <tr key={student.uid} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-9 h-9 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 shrink-0">
                            <StudentAvatar studentId={student.uid} studentName={student.name} studentEmail={student.email} size="sm" />
                          </div>
                          <div className="min-w-0">
                            <span className="font-black text-slate-900 block truncate">{student.name || 'Unnamed Student'}</span>
                            <span className="text-[10px] text-slate-400 block truncate">{student.email}</span>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="text-slate-800 font-semibold truncate max-w-[200px]">{student.school || 'Unassigned Campus'}</div>
                        <div className="text-[10px] text-slate-400 uppercase truncate">{student.district || student.tenantId || 'Regional District'}</div>
                      </td>

                      <td className="py-3.5 px-4 font-mono text-slate-600">
                        {student.dateOfBirth ? formatDateOfBirth(student.dateOfBirth) : (
                          <span className="italic text-amber-600 font-semibold text-[11px]">Not provided</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 font-bold">
                        {age !== null ? (
                          <span className={`px-2 py-0.5 rounded text-[10px] ${
                            isMinor ? 'bg-sky-50 text-sky-700 border border-sky-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}>
                            {age} yrs ({isMinor ? 'Minor' : 'Adult'})
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4">
                        {isMinor && daysUntil18 !== null ? (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-black ${
                            daysUntil18 <= 30 ? 'bg-rose-50 text-rose-800 border border-rose-200 animate-pulse' :
                            daysUntil18 <= 90 ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}>
                            <Clock className="w-3.5 h-3.5" />
                            <span>{statusText}</span>
                          </span>
                        ) : !isMinor && age !== null ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-bold text-[11px]">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                            <span>Adult (No Consent Gate Required)</span>
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">Age unknown</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <button
                          type="button"
                          onClick={() => onSelectUser(student)}
                          className="px-3 py-1.5 bg-[#0094c8]/10 hover:bg-[#0094c8] text-[#0094c8] hover:text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer"
                        >
                          Manage Profile →
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400 italic">
                    No students found matching the selected filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
